// ContentIQ Home — "What do you want to create today?" The wizard-first
// front door (Edward, 2026-08-20): resume lane + one card per deliverable,
// each entering the SAME wizard spine at its own depth via the prereq
// check. All projects and ContentIQ config stay one click away. All
// look-and-feel comes from utils/wizardTheme.js.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { canAccess, useCurrentUser } from '../../context/UserContext';
import CiqStageChip from '../../components/CiqStageChip';
import DeliverableCard from '../../components/DeliverableCard';
import {
  BRAND, FONTS, HUES, INK, NEUTRAL, RADII, SHADOW, STATE,
  buttonLink, buttonPrimary, buttonSecondary, card, pageShell, pill,
} from '../../utils/wizardTheme';
import { PHASES } from '../../utils/ciqRail';

const API = process.env.REACT_APP_API_URL;

// The path every project walks — the same four phases as the project rail
// (Edward, 2026-10-22: the strip had drifted from the rail), exited early or late.
const PHASE_BG = { gather: BRAND.sky, content: BRAND.link, build: HUES.lp.main, launchg: STATE.done.main };
const SPINE = PHASES.map(([key, , label]) => ({ label, bg: PHASE_BG[key] }));

function icon(path) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={NEUTRAL.pageBg}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{path}</svg>
  );
}

// The lanes (strategy reset R1, Edward 2026-10-15; lane 3 "Landing page build"
// from your own copy added 2026-10-22): generate the
// content strategy, generate the pages, generate the sales-enablement doc
// (modeled now, built in a later round). One shared spine underneath —
// personas live inside strategy work now, and scripts are a section of the
// strategy document, not standalone deliverables. Exported so a lane's Start
// page wears the same icon as its Home card.
export const CARDS = [
  {
    output: 'strategy', hue: HUES.strategy, title: 'Content strategy',
    description: 'The client-ready strategy document — pages, copy, qualification flow, and the calling scripts where the product includes them.',
    icon: icon(<>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
    </>),
  },
  {
    output: 'lp', hue: HUES.lp, title: 'Landing page copy',
    description: 'Strategy first, then each landing page\u2019s exact copy — download it, or send it on to the build team.',
    icon: icon(<>
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </>),
  },
  {
    output: 'pagebuild', hue: HUES.lp, title: 'Landing page build',
    description: 'Already have the copy from outside ContentIQ? Bring it in and send the pages straight to the build team — built, checked, approved, launched.',
    icon: icon(<>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
    </>),
  },
  {
    output: 'salesdeck', hue: HUES.scripts, title: 'Sales enablement deck',
    description: 'A follow-up guide for the client\u2019s sales team — how the leads were qualified, who they are, how to follow up. For Deal Accelerator, Decision Builder+ and Demand Cascade projects.',
    // Built 2026-10-23 (v1): a deck belongs to a project, so the card opens a
    // project picker instead of the Start page.
    path: '/contentiq/deck',
    icon: icon(<>
      <path d="M2 3h20" /><rect x="4" y="3" width="16" height="12" rx="1" />
      <polyline points="7 11 10 8 13 10 17 6" />
      <line x1="12" y1="15" x2="12" y2="19" />
      <line x1="8" y1="21" x2="12" y2="19" /><line x1="16" y1="21" x2="12" y2="19" />
    </>),
  },
];

// The rail step each legacy stage number lands on — the old "Stage N"
// numbering is gone from the screens (the rail names the steps).
const STAGE_RAIL_NAME = { 1: 'Research', 2: 'Buyer map', 3: 'Content strategy', 4: 'Page build' };

// Where a project stands, for the resume lane's status pill (fallback for
// payloads without the computed 7-step ladder).
export function projectStatus(pj) {
  const stages = pj.stages || [];
  const s4 = stages.find(s => s.stage_number === 4);
  if (s4?.done) return { label: 'complete', fg: STATE.done.text, bg: STATE.done.tint };
  const next = stages.find(s => !s.done);
  return next
    ? { label: `at ${STAGE_RAIL_NAME[next.stage_number] || next.stage_name}`, fg: INK.secondary, bg: NEUTRAL.chipBg }
    : { label: 'complete', fg: STATE.done.text, bg: STATE.done.tint };
}

export default function ContentIQHome() {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser() || {};
  const [projects, setProjects] = useState([]);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    axios.get(`${API}/contentiq/projects/`).then(r => setProjects(r.data)).catch(() => setLoadErr(true));
  }, []);
  // ContentIQ config is admin-only; a PM following the link was bounced to
  // NeTrA Home with no message (usability pass, 2026-10-22).
  const headerLinks = [
    ['Content assets', '/contentiq/assets'],
    ...(canAccess(currentUser?.role, 'contentiq-admin') ? [['ContentIQ config', '/contentiq/admin']] : []),
  ];

  // The list arrives recently-worked-first; active projects lead the lane.
  // A launched project (ladder stage 7) is done — not something to resume.
  const inFlight = projects
    .filter(pj => (pj.stages || []).some(s => !s.done))
    .filter(pj => pj.stage?.key !== 'launched')
    .slice(0, 2);

  return (
    <div style={pageShell}>
      <div style={{
        maxWidth: '1010px', margin: '0 auto', padding: '34px 48px 48px',
        boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '30px',
      }}>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flexGrow: 1 }}>
            <div style={{
              fontFamily: FONTS.display, fontSize: '42px', fontWeight: 700,
              lineHeight: 1.1, letterSpacing: '-0.5px',
            }}>
              What do you want<br />to <span style={{ color: BRAND.button }}>create</span> today?
            </div>
            <div style={{ fontSize: '14px', color: INK.secondary, maxWidth: '540px', lineHeight: 1.6 }}>
              Start from scratch with a strategy, write the landing-page copy, or bring
              copy you already have straight to the build team.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', paddingBottom: '6px' }}>
            {/* "Advanced" deliberately absent — it duplicated "All projects →"
                in the resume lane below (Edward, 2026-08-24). */}
            {headerLinks.map(([label, path]) => (
              <button key={path} onClick={() => navigate(path)}
                style={{ ...buttonLink, color: INK.secondary }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* The FIRST option (Edward, 2026-08-24): start a project outright —
            the full-engagement front door, ahead of the per-deliverable cards. */}
        <div onClick={() => navigate('/contentiq/new')}
          style={{
            background: BRAND.button, borderRadius: RADII.card, padding: '18px 26px',
            display: 'flex', alignItems: 'center', gap: '18px', cursor: 'pointer',
            boxShadow: SHADOW.focus,
          }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: RADII.tile, background: NEUTRAL.pageBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: BRAND.button, fontSize: '24px', fontWeight: 700, flexShrink: 0,
          }}>
            +
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexGrow: 1 }}>
            <div style={{ fontFamily: FONTS.display, fontSize: '17px', fontWeight: 600, color: NEUTRAL.pageBg }}>
              Not sure which? Start a full project
            </div>
            <div style={{ fontSize: '12.5px', color: INK.muted }}>
              Start from a NeTrA program and walk the full path — research to launch. Or pick one deliverable below.
            </div>
          </div>
          <button onClick={e => { e.stopPropagation(); navigate('/contentiq/new'); }}
            style={{ ...buttonSecondary, border: 'none', boxShadow: SHADOW.card }}>
            Start →
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '18px' }}>
          {CARDS.map(c => (
            <DeliverableCard key={c.output} hue={c.hue} icon={c.icon} title={c.title}
              description={c.description} depth={c.depth} locked={c.locked}
              cta={{ label: 'Create', onClick: () => navigate(c.path || `/contentiq/start/${c.output}`) }} />
          ))}
        </div>

        <div style={{
          background: BRAND.navy, borderRadius: RADII.card, padding: '20px 26px',
          display: 'flex', alignItems: 'center', gap: '18px',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '220px', flexShrink: 0 }}>
            <div style={{ fontFamily: FONTS.display, fontSize: '15px', fontWeight: 600, color: NEUTRAL.pageBg }}>
              One shared foundation
            </div>
            <div style={{ fontSize: '12px', color: INK.muted, lineHeight: 1.5 }}>
              Whichever you pick, the wizard walks the same path — only as far as it needs.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {SPINE.map((node, i) => (
              <React.Fragment key={node.label}>
                {i > 0 && <div style={{ height: '2px', background: BRAND.navyLine, width: '24px' }} />}
                {node.gate ? (
                  <div style={{
                    background: BRAND.navy, color: STATE.gate.onNavy, border: `2px dashed ${STATE.gate.strong}`,
                    borderRadius: RADII.pill, padding: '5px 14px', fontSize: '12px', fontWeight: 700,
                  }}>
                    {node.label}
                  </div>
                ) : (
                  <div style={{
                    background: node.bg, color: NEUTRAL.pageBg, borderRadius: RADII.pill,
                    padding: '7px 16px', fontSize: '12px', fontWeight: 600,
                  }}>
                    {node.label}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
            <div style={{ fontFamily: FONTS.display, fontSize: '20px', fontWeight: 600 }}>
              Pick up where you left off
            </div>
            <button onClick={() => navigate('/contentiq/advanced')} style={buttonLink}>
              All projects →
            </button>
          </div>
          {loadErr ? (
            <div role="alert" style={{ fontSize: '13px', color: STATE.danger.text }}>
              Couldn&rsquo;t load your projects — refresh the page to try again.
            </div>
          ) : inFlight.length === 0 ? (
            <div style={{ fontSize: '13px', color: INK.muted }}>
              Nothing in flight — pick a deliverable above to start.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '18px' }}>
              {inFlight.map(pj => {
                const status = projectStatus(pj);
                const att = (pj.attachments || [])[0];
                return (
                  <div key={pj.id} style={{
                    ...card, padding: '18px 22px',
                    display: 'flex', alignItems: 'center', gap: '16px',
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexGrow: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '14.5px' }}>{pj.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: INK.secondary, flexWrap: 'wrap' }}>
                        {att && <span>{att.client_name ? `${att.client_name} · ` : ''}{att.program_name}</span>}
                        {/* The 7-step ladder (design settled 2026-10-08) when the
                            backend sends it; the old stage pill as fallback. */}
                        {pj.stage
                          ? <CiqStageChip stage={pj.stage} />
                          : <span style={pill(status.fg, status.bg)}>{status.label}</span>}
                      </div>
                    </div>
                    {/* One interface (2026-08-26): the project screen lands by
                        state itself — Resume needs no routing smarts here. */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '5px' }}>
                      <button
                        onClick={() => navigate(`/contentiq/project/${pj.id}`)}
                        style={{ ...buttonPrimary(), padding: '9px 18px', boxShadow: SHADOW.card }}>
                        Resume
                      </button>
                      <button onClick={() => navigate(`/contentiq/project/${pj.id}?section=review`)}
                        style={{ ...buttonLink, fontSize: '11.5px', textAlign: 'center' }}>
                        Open Content plan
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
